import React from 'react'
import {useRouter} from 'next/router'

import {useAsync} from '@lib/hooks'
import {getIp, sendEmail} from '@lib/services'
import {acceptConsent} from '@lib/utils/consent'
import {useRecaptcha} from '@lib/recaptcha'
import {logEvent, useUTMContext} from '@lib/gtm'

import {MarketingCheckbox, PrivacyNotice} from '@components/forms'
import {validateEmail} from '@lib/helpers'

type Input = {value: string}

export const ContactForm = () => {
  const router = useRouter()
  const [accept, setAccept] = React.useState(false)
  const {run: runSendEmail, isLoading, isSuccess} = useAsync(sendEmail)
  const {isRecaptchaValidated} = useRecaptcha()
  const ref = React.useRef<HTMLFormElement>()
  const {data: utm} = useUTMContext()
  const {run: runGetIp} = useAsync(getIp)

  React.useEffect(() => {
    if (isSuccess && ref.current) {
      ref.current.reset()
      setAccept(false)

      logEvent({
        ga: {
          category: 'Submit Form Success',
          action: 'Submit Form Success',
          label: router.pathname.replace('/', '').replaceAll('-', ' '),
        },
        fb: {
          event: 'Lead',
        },
      })
    }
  }, [isSuccess])

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const target = e.target as typeof e.target & {
      email: Input
      name: Input
      company: Input
      phone: Input
      message: Input
      accept: {checked: boolean}
    }

    const {email, name, company, phone, message, accept} = target
    const ip = await runGetIp({})

    const emailData = {
      to: process.env.SALES_EMAIL,
      subject: `[AI consultation LP - ${window.location.host}] ${email.value} wants to contact with you`,
      message: `
        Name: ${name.value}
        Company: ${company.value}
        Email: ${email.value}
        Tel: (+66) ${phone.value}
        Message: ${message.value}
        IP Address: ${ip}
      `,
    }

    const reqBody = {
      email: email.value,
      name: name.value,
      company: company.value,
      phone: phone.value,
      message: message.value,
      url: window.location.href,
      marketing_consent: accept?.checked,
    }

    const res = await runSendEmail({
      emailData: {...emailData, path: process.env.EMAIL_API_URL},
      reqBody: reqBody,
      utm,
    })

    if (res.status === 200 && accept.checked) {
      const [firstName, ...rest] = name.value.split(' ')
      const lastName = rest.join(' ') || null

      await acceptConsent({
        apiKey: process.env.CONSENT_WOW_CONTACT_API_KEY,
        purposeId: process.env.CONSENT_WOW_MARKETING_CONSENT_PURPOSE_ID,
        data: {
          ...reqBody,
          name: firstName,
          surname: lastName,
          phone_number: phone.value,
        },
      })
    }
  }

  return (
    <div className='AI__contact__container' id='contact'>
      <h5>ให้เราได้เป็นส่วนหนึ่งในการพัฒนาธุรกิจของคุณ</h5>
      <form ref={ref} className='AI__contact__form' onSubmit={handleOnSubmit}>
        <div className='AI__contact__form__row'>
          <input name='name' placeholder='ชื่อ - นามสกุล' required />
          <input
            type='email'
            name='email'
            placeholder='อีเมล'
            onBlur={validateEmail}
            required
          />
        </div>

        <div className='AI__contact__form__row'>
          <input name='company' placeholder='ชื่อบริษัท' required />
          <div className='AI__contact__form__input-phone'>
            <IconTH />
            <span>+66</span>
            <input
              type='tel'
              name='phone'
              pattern='\d{10}'
              title='Please enter a 10-digit phone number'
              required
            />
          </div>
        </div>

        <textarea
          name='message'
          rows={3}
          placeholder='ข้อความ'
          required
        ></textarea>

        <MarketingCheckbox
          name='accept'
          className='AI__contact__form__checkbox'
          isChecked={accept}
          onClick={() => {
            setAccept((prev) => !prev)
          }}
        />

        <div className='AI__contact__form__submit'>
          <button type='submit' disabled={isLoading || !isRecaptchaValidated}>
            <span>
              {isSuccess
                ? 'ดำเนินการเรียบร้อย'
                : isLoading
                ? 'กำลังดำเนินการ...'
                : !isRecaptchaValidated
                ? 'กรุณารอสักครู่...'
                : 'ติดต่อเรา'}
            </span>
          </button>
        </div>

        <PrivacyNotice
          name='privacy-notice'
          className='AI__contact__form__text'
        />
      </form>
    </div>
  )
}

const IconTH = () => {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path d='M1 4.5h22v15H1v-15z' fill='#D50000' />
      <path d='M1 7h22v10H1V7z' fill='#ECEFF1' />
      <path d='M1 9.5h22v5H1v-5z' fill='#1A237E' />
    </svg>
  )
}
